import { csrfCookie } from '../../shared/kit';

let REST_BASE = '';
let NONCE = '';

export function configureApi(restBase: string, nonce: string): void {
    REST_BASE = restBase.replace(/\/$/, '');
    NONCE = nonce;
}

export interface LineAvailability {
    available: number;
    quote: { applied: number; normal: number; member: number | null; free: boolean } | null;
}

function authHeaders(method: string, idempotencyKey?: string): Record<string, string> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    const nonce = String(NONCE || '').trim();
    if (nonce !== '') {
        headers['X-WP-Nonce'] = nonce;
    }
    if (method !== 'GET' && method !== 'HEAD') {
        const csrf = csrfCookie();
        if (csrf !== '') {
            headers['X-GAS-CSRF'] = csrf;
        } else if (nonce === '') {
            throw new Error('Jeton de sécurité indisponible');
        }
        if (idempotencyKey) {
            headers['Idempotency-Key'] = idempotencyKey;
        }
    }
    return headers;
}

function newIdempotencyKey(): string {
    const webCrypto = globalThis.crypto;
    if (typeof webCrypto?.randomUUID === 'function') {
        return `rental:${webCrypto.randomUUID()}`;
    }
    if (typeof webCrypto?.getRandomValues === 'function') {
        const bytes = new Uint8Array(24);
        webCrypto.getRandomValues(bytes);
        return `rental:${Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('')}`;
    }
    return `rental:${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

async function get<T>(path: string): Promise<T> {
    const res = await fetch(`${REST_BASE}${path}`, {
        headers: authHeaders('GET'),
        credentials: 'same-origin',
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json?.message || `HTTP ${res.status}`);
    return (json?.data ?? json) as T;
}

export interface BusyPeriod { start: string; end: string; quantity: number }
export interface BatchCalendarItem { capacity: number; name: string; busy: BusyPeriod[] }
export interface BatchCalendar { from: string; to: string; items: Record<string, BatchCalendarItem> }

export function fetchBatchCalendar(itemIds: number[], from: string, to: string): Promise<BatchCalendar> {
    const qs = `?item_ids=${itemIds.join(',')}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
    return get<BatchCalendar>(`/calendar${qs}`);
}

export interface RentalItemInfo { id: number; name: string; slug: string; image_url: string | null }
export function fetchItem(itemId: number): Promise<RentalItemInfo> {
    return get<RentalItemInfo>(`/items/${itemId}`);
}

export function fetchAvailability(itemId: number, start: string, end: string, qty: number): Promise<LineAvailability> {
    const qs = `?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}&quantity=${qty}`;
    return get<LineAvailability>(`/items/${itemId}/availability${qs}`);
}

/**
 * Crée N réservations en UNE caisse (panier multi-périodes) — une par segment.
 * Tout ou rien côté serveur : un segment refusé annule les autres.
 */
export async function createReservationsBatch(
    segments: Array<{
        start_at: string;
        end_at: string;
        lines: Array<{ item_id: number; quantity: number }>;
    }>,
    termsAccepted = false,
    queryId = '',
    idempotencyKey = newIdempotencyKey(),
): Promise<{ reservation_ids: number[] }> {
    const body: Record<string, unknown> = {
        segments,
        terms_accepted: termsAccepted,
    };
    if (queryId.trim()) body.query_id = queryId.trim();

    const res = await fetch(`${REST_BASE}/reservations/batch`, {
        method: 'POST',
        headers: { ...authHeaders('POST', idempotencyKey), 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new Error(String(json?.message || json?.data?.message || 'batch_failed'));
    }
    const payload = json?.data ?? json;
    return { reservation_ids: (payload?.reservation_ids ?? []).map((n: unknown) => Number(n)) };
}

export async function createReservation(
    start: string,
    end: string,
    lines: Array<{ item_id: number; quantity: number }>,
    termsAccepted = false,
    queryId = '',
    idempotencyKey = newIdempotencyKey(),
): Promise<{ reservation_id: number }> {
    const body: Record<string, unknown> = {
        start_at: start,
        end_at: end,
        lines,
        terms_accepted: termsAccepted,
    };
    if (queryId.trim()) body.query_id = queryId.trim();

    const res = await fetch(`${REST_BASE}/reservations`, {
        method: 'POST',
        headers: { ...authHeaders('POST', idempotencyKey), 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json?.message || json?.data?.message || `HTTP ${res.status}`);
    return (json?.data ?? json) as { reservation_id: number };
}

export async function joinWaitlist(
    itemId: number,
    start: string,
    end: string,
    idempotencyKey = newIdempotencyKey(),
): Promise<{ ok: boolean; already?: boolean }> {
    const res = await fetch(`${REST_BASE}/items/${itemId}/waitlist`, {
        method: 'POST',
        headers: { ...authHeaders('POST', idempotencyKey), 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ start, end }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json?.message || `HTTP ${res.status}`);
    return (json?.data ?? json) as { ok: boolean; already?: boolean };
}

/** Empreinte logique d'une tentative panier (dates, lignes, conditions, query_id). */
export function fingerprintCartAttempt(parts: {
    segments: Array<{
        start_at: string;
        end_at: string;
        lines: Array<{ item_id: number; quantity: number }>;
    }>;
    terms_accepted: boolean;
    query_id?: string;
}): string {
    const segments = parts.segments.map((seg) => ({
        start_at: seg.start_at,
        end_at: seg.end_at,
        lines: seg.lines
            .map((l) => ({ item_id: l.item_id, quantity: l.quantity }))
            .sort((a, b) => a.item_id - b.item_id || a.quantity - b.quantity),
    }));
    return JSON.stringify({
        segments,
        terms_accepted: !!parts.terms_accepted,
        query_id: String(parts.query_id ?? '').trim(),
    });
}

export type AttemptKeyState = { fingerprint: string; key: string };

export function resolveAttemptKey(
    current: AttemptKeyState | null,
    fingerprint: string,
    mint: () => string = newIdempotencyKey,
): AttemptKeyState {
    if (current && current.fingerprint === fingerprint) {
        return current;
    }
    return { fingerprint, key: mint() };
}

export { newIdempotencyKey };
